import { provide } from 'vue';
import {
  MOUNT_PARCEL_KEY,
  PARCEL_REMOTES_KEY,
  type MountParcel,
  type ParcelRemotes,
} from '@platform/parcels/types';
import {
  getActiveOverrides,
  getActiveOverridesPerScope,
  isDevMode,
  type OverrideResult,
} from '@platform/parcels/dev-tools';

export interface ParcelConsumerOptions {
  /** single-spa mountParcel function — passed as a prop by the shell */
  mountParcel: MountParcel;
  /** Scope-to-URL map — typically auto-generated by generate-parcel-config */
  remotes: ParcelRemotes;
}

/**
 * Detect parcel overrides from URL params, localStorage, or environment.
 * Priority: URL param > localStorage > environment.
 *
 * @deprecated Use `getActiveOverrides()` from `@abax/platform` dev-tools instead.
 * This function delegates to `getActiveOverrides()` for backward compatibility.
 */
export function detectParcelOverrides(): OverrideResult {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      '[abax/platform] detectParcelOverrides is deprecated. Use getActiveOverrides() instead.',
    );
  }
  return getActiveOverrides();
}

function logOverrides(source: string, overrides: ParcelRemotes): void {
  if (!isDevMode()) return;

  console.groupCollapsed(`[parcels] Overrides active (${source})`);
  Object.entries(overrides).forEach(([scope, url]) => console.log(`  ${scope} → ${url}`));
  console.groupEnd();
}

/**
 * One-call setup for parcel consumers. Provides both the mountParcel function
 * and the remotes registry so descendant ParcelLoader components can resolve
 * and mount remote parcels.
 *
 * Automatically detects runtime overrides from URL params, localStorage, or env.
 * No override code needed in consumers.
 *
 * Call once in app root setup:
 * ```ts
 * import { setupParcelConsumer } from '@abax/platform';
 * import { parcelRemotes } from '@/config/parcel-config';
 *
 * const props = defineProps<{ mountParcel: MountParcel }>();
 * setupParcelConsumer({ mountParcel: props.mountParcel, remotes: parcelRemotes });
 * ```
 */
export function setupParcelConsumer({ mountParcel, remotes }: ParcelConsumerOptions): void {
  const { overrides, sources } = getActiveOverridesPerScope();
  const hasOverrides = Object.keys(overrides).length > 0;
  const effectiveRemotes = hasOverrides ? { ...remotes, ...overrides } : remotes;

  // Debug logging for override detection (dev mode only)
  if (isDevMode()) {
    console.debug('[parcels] setupParcelConsumer called', {
      hasOverrides,
      sources,
      overrides,
      originalRemotes: remotes,
      effectiveRemotes,
    });
  }

  if (hasOverrides) {
    // Group overrides by source for readable logging
    const bySource = new Map<string, ParcelRemotes>();
    for (const [scope, url] of Object.entries(overrides)) {
      const src = sources[scope] ?? 'unknown';
      if (!bySource.has(src)) bySource.set(src, {});
      bySource.get(src)![scope] = url;
    }
    for (const [source, scopeOverrides] of bySource) {
      logOverrides(source, scopeOverrides);
    }
  }

  provide(MOUNT_PARCEL_KEY, mountParcel);
  // Symbol key gets un-merged static config — ParcelLoader resolves overrides dynamically
  // via getActiveOverridesPerScope() on each load attempt (R-36)
  provide(PARCEL_REMOTES_KEY, remotes);

  // Legacy string keys for backwards compatibility with old parcel-wrapper components
  provide('mountParcel', mountParcel);
  // Legacy string key keeps merged value for backward compat (R-11)
  provide('parcelRemotes', effectiveRemotes);
}
